export const widgetCss = `
/*
  Widget styles are injected into a ShadowRoot.
  Keep everything self-contained and token-driven.
*/

:host, :host * { box-sizing: border-box; }

#weyni-widget {
  --weyni-primary: #0B66D4;
  --weyni-radius: 10px;
  --weyni-launcher-text: #ffffff;

  --weyni-bg: #ffffff;
  --weyni-fg: rgba(15, 23, 42, 0.92);
  --weyni-muted: rgba(15, 23, 42, 0.62);
  --weyni-border: rgba(15, 23, 42, 0.14);
  --weyni-surface: #ffffff;
  --weyni-surface-2: rgba(15, 23, 42, 0.04);
  --weyni-shadow: 0 1px 0 rgba(15, 23, 42, 0.10);
  --weyni-shadow-2: 0 1px 0 rgba(15, 23, 42, 0.14);
  --weyni-focus: rgba(11, 102, 212, 0.22);
}

#weyni-widget[data-theme="dark"] {
  --weyni-bg: #0f172a;
  --weyni-fg: rgba(248, 250, 252, 0.92);
  --weyni-muted: rgba(248, 250, 252, 0.62);
  --weyni-border: rgba(248, 250, 252, 0.14);
  --weyni-surface: #111b31;
  --weyni-surface-2: rgba(248, 250, 252, 0.06);
  --weyni-shadow: 0 1px 0 rgba(0,0,0,0.60);
  --weyni-shadow-2: 0 1px 0 rgba(0,0,0,0.70);
  --weyni-focus: rgba(11, 102, 212, 0.30);
}

@media (prefers-color-scheme: dark) {
  #weyni-widget[data-theme-mode="system"] {
    --weyni-bg: #0f172a;
    --weyni-fg: rgba(248, 250, 252, 0.92);
    --weyni-muted: rgba(248, 250, 252, 0.62);
    --weyni-border: rgba(248, 250, 252, 0.14);
    --weyni-surface: #111b31;
    --weyni-surface-2: rgba(248, 250, 252, 0.06);
    --weyni-shadow: 0 1px 0 rgba(0,0,0,0.60);
    --weyni-shadow-2: 0 1px 0 rgba(0,0,0,0.70);
    --weyni-focus: rgba(11, 102, 212, 0.30);
  }
}

#weyni-widget {
  position: fixed;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 2147483647;
  font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
  letter-spacing: 0.01em;
  color: var(--weyni-fg);

  /*
    Prevent host-page styles (e.g. text-align / direction) from changing launcher alignment.
    Also keep the launcher pinned to the configured side when the panel opens.
  */
  direction: ltr;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
}

#weyni-widget[data-position="right"] { right: calc(16px + env(safe-area-inset-right, 0px)); }
#weyni-widget[data-position="left"] {
  left: calc(16px + env(safe-area-inset-left, 0px));
  align-items: flex-start;
}

.weyni-button {
  appearance: none;
  border: 0;
  cursor: pointer;
  border-radius: 999px;
  height: 48px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--weyni-primary);
  color: var(--weyni-launcher-text);
  box-shadow: none;
  position: relative;
}

.weyni-button[data-mode="ICON_ONLY"] {
  width: 48px;
  padding: 0;
  justify-content: center;
}

.weyni-button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px var(--weyni-focus);
}

.weyni-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(255,255,255,0.9);
}

.weyni-launcher-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.weyni-launcher-icon-img {
  height: 18px;
  width: auto;
  max-width: 24px;
  display: block;
}

.weyni-launcher-text {
  font-weight: 600;
}

.weyni-badge {
  margin-left: 2px;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  background: rgba(255,255,255,0.22);
  border: 1px solid rgba(255,255,255,0.28);
}

.weyni-panel {
  width: min(380px, calc(100vw - 32px));
  height: min(560px, calc(100dvh - 120px));
  border-radius: var(--weyni-radius);
  border: 1px solid var(--weyni-border);
  background: var(--weyni-surface);
  box-shadow: var(--weyni-shadow);
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr auto auto;
  transform-origin: bottom right;
  transition: transform 160ms ease, opacity 160ms ease;
}

#weyni-widget[data-position="left"] .weyni-panel {
  transform-origin: bottom left;
}

.weyni-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px;
  border-bottom: 1px solid var(--weyni-border);
  background: var(--weyni-surface);
}

.weyni-title {
  display: grid;
  gap: 2px;
}

.weyni-title strong {
  font-size: 13px;
  letter-spacing: 0.2px;
}

.weyni-title span {
  font-size: 12px;
  color: var(--weyni-muted);
}

.weyni-typing {
  font-size: 12px;
  color: var(--weyni-muted);
}

.weyni-close {
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  padding: 6px 10px;
  border-radius: 10px;
  color: var(--weyni-fg);
}

.weyni-close:hover { background: rgba(0,0,0,0.06); }

#weyni-widget[data-theme="dark"] .weyni-close:hover { background: rgba(255,255,255,0.08); }

@supports (color: color-mix(in srgb, white, black)) {
  .weyni-close:hover { background: color-mix(in srgb, var(--weyni-fg) 6%, transparent); }
}
.weyni-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px var(--weyni-focus);
}

@supports (color: color-mix(in srgb, white, black)) {
  .weyni-close:focus-visible {
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--weyni-primary) 22%, transparent);
  }
}

.weyni-messages {
  padding: 12px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--weyni-surface);
}

.weyni-bubble {
  max-width: 85%;
  padding: 10px 12px;
  border-radius: calc(var(--weyni-radius) - 2px);
  font-size: 13px;
  line-height: 1.35;
  border: 1px solid var(--weyni-border);
  white-space: pre-wrap;
  word-break: break-word;
}

.weyni-bubble.ai {
  align-self: flex-start;
  background: var(--weyni-surface-2);
}

.weyni-bubble.agent {
  align-self: flex-start;
  background: var(--weyni-surface-2);
}

.weyni-bubble.visitor {
  align-self: flex-end;
  background: rgba(11, 102, 212, 0.10);
  border-color: rgba(11, 102, 212, 0.20);
}

@supports (color: color-mix(in srgb, white, black)) {
  .weyni-bubble.visitor {
    background: color-mix(in srgb, var(--weyni-primary) 14%, transparent);
    border-color: color-mix(in srgb, var(--weyni-primary) 26%, var(--weyni-border));
  }
}

.weyni-composer {
  padding: 10px;
  border-top: 1px solid var(--weyni-border);
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  background: var(--weyni-surface);
}

.weyni-actions {
  padding: 10px;
  border-top: 1px solid var(--weyni-border);
  background: var(--weyni-surface);
  display: flex;
  justify-content: flex-start;
}

.weyni-secondary {
  height: 32px;
  border-radius: calc(var(--weyni-radius) - 6px);
  border: 1px solid var(--weyni-border);
  padding: 0 10px;
  cursor: pointer;
  background: transparent;
  color: var(--weyni-fg);
  font-size: 12px;
}

.weyni-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.weyni-secondary:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px var(--weyni-focus);
}

.weyni-input {
  height: 40px;
  border-radius: calc(var(--weyni-radius) - 4px);
  border: 1px solid var(--weyni-border);
  padding: 0 12px;
  font-size: 13px;
  outline: none;
  background: var(--weyni-bg);
  color: var(--weyni-fg);
}

#weyni-widget[data-theme="dark"] .weyni-input {
  background: rgba(255,255,255,0.04);
}

@supports (color: color-mix(in srgb, white, black)) {
  .weyni-input {
    background: color-mix(in srgb, var(--weyni-surface-2) 70%, transparent);
  }
}

.weyni-input:focus {
  border-color: rgba(11, 102, 212, 0.65);
  box-shadow: 0 0 0 4px var(--weyni-focus);
}

.weyni-textarea {
  border-radius: calc(var(--weyni-radius) - 4px);
  border: 1px solid var(--weyni-border);
  padding: 10px 12px;
  font-size: 13px;
  outline: none;
  resize: vertical;
  background: var(--weyni-bg);
  color: var(--weyni-fg);
}

@supports (color: color-mix(in srgb, white, black)) {
  .weyni-textarea {
    background: color-mix(in srgb, var(--weyni-surface-2) 70%, transparent);
  }
}

.weyni-textarea:focus {
  border-color: rgba(11, 102, 212, 0.65);
  box-shadow: 0 0 0 4px var(--weyni-focus);
}

@supports (color: color-mix(in srgb, white, black)) {
  .weyni-textarea:focus {
    border-color: color-mix(in srgb, var(--weyni-primary) 55%, var(--weyni-border));
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--weyni-primary) 18%, transparent);
  }
}

.weyni-offline {
  padding: 10px;
  border-top: 1px solid var(--weyni-border);
  background: var(--weyni-surface);
  display: grid;
  gap: 8px;
}

.weyni-offline-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--weyni-fg);
}

.weyni-offline-sub {
  font-size: 12px;
  color: var(--weyni-muted);
}

.weyni-handoff {
  padding: 10px;
  border-top: 1px solid var(--weyni-border);
  background: var(--weyni-surface);
  display: grid;
  gap: 8px;
}

.weyni-handoff-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--weyni-fg);
}

.weyni-handoff-sub {
  font-size: 12px;
  color: var(--weyni-muted);
}

.weyni-handoff-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

@supports (color: color-mix(in srgb, white, black)) {
  .weyni-input:focus {
    border-color: color-mix(in srgb, var(--weyni-primary) 55%, var(--weyni-border));
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--weyni-primary) 18%, transparent);
  }
}

.weyni-send {
  height: 40px;
  border-radius: calc(var(--weyni-radius) - 4px);
  border: 0;
  padding: 0 12px;
  cursor: pointer;
  background: var(--weyni-primary);
  color: white;
  font-size: 13px;
}

.weyni-send:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.weyni-powered {
  padding: 10px 12px;
  border-top: 1px solid var(--weyni-border);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  background: var(--weyni-surface);
  color: var(--weyni-muted);
  font-size: 11px;
  text-decoration: none;
}

.weyni-powered:hover {
  color: var(--weyni-fg);
}

.weyni-powered:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px var(--weyni-focus);
}

.weyni-powered-logo {
  width: 14px;
  height: 14px;
  display: block;
}

.weyni-notice {
  border: 1px dashed var(--weyni-border);
  border-radius: calc(var(--weyni-radius) - 4px);
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--weyni-muted);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.weyni-link {
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: var(--weyni-primary);
  font-weight: 600;
}

.weyni-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px var(--weyni-focus);
  border-radius: 8px;
}

@supports (color: color-mix(in srgb, white, black)) {
  .weyni-link:focus-visible {
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--weyni-primary) 18%, transparent);
  }
}

@media (max-width: 420px) {
  .weyni-panel {
    width: calc(100vw - 24px);
    height: min(640px, calc(100dvh - 96px));
  }
}

/*
  Mobile: open state should feel like a native full-screen sheet.
  We keep the launcher pinned, but the panel itself covers the viewport.
*/
@media (max-width: 768px), (max-height: 520px) {
  .weyni-panel {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100dvh;
    border-radius: 0;
    border-left: 0;
    border-right: 0;
    border-bottom: 0;
  }

  .weyni-header {
    padding-top: calc(12px + env(safe-area-inset-top, 0px));
  }

  .weyni-composer {
    padding-bottom: 10px;
  }

  .weyni-powered {
    padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  }
}

@media (prefers-reduced-motion: reduce) {
  .weyni-panel { transition: none; }
}
`;
